import { zodResolver } from "@hookform/resolvers/zod"
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  BuildingIcon,
  Loader2,
} from "lucide-react"
import { useEffect, useMemo, useRef, useState } from "react"
import { useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"

import BrandLogo from "@/components/commons/app-brand-logo"
import { Button } from "@/components/ui/button"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { PasswordInput } from "@/components/ui/password-input"
import { FRONTEND_ROUTES } from "@/constants"
import { useAuth } from "@/contexts/auth"
import { useWhitelabel } from "@/contexts/whitelabel"
import { useSSOAuth, useToast } from "@/hooks"
import type { ForgotPasswordFormSchemaProps } from "@/schemas/forgot-form"
import { forgotPasswordFormSchema } from "@/schemas/forgot-form"
import {
  type FormSchemaSignIn,
  formSchemaSignIn,
} from "@/schemas/signin-form"
import { StringHelper } from "@/utils"
const LoginForm = () => {
  const { t } = useTranslation()
  const { toast } = useToast()
  const { login } = useAuth()
  const { configuration } = useWhitelabel()
  const [isSSO, setIsSSO] = useState(!!configuration)
  const userToggledRef = useRef(false)

  useEffect(() => {
    if (configuration && !userToggledRef.current) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setIsSSO(true)
    }
  }, [configuration])

  const handleToggleToLogin = () => {
    userToggledRef.current = true
    setIsSSO(false)
  }

  const handleToggleToSSO = () => {
    userToggledRef.current = true
    setIsSSO(true)
  }

  const {
    mutateAsync: fetchSSOUrl,
    isPending: isSSOPending,
  } = useSSOAuth()
  const brand = import.meta.env.VITE_BRAND ?? "pay2b"
  const loginSchema = useMemo(
    () => formSchemaSignIn(t),
    [t]
  )
  const ssoSchema = useMemo(
    () => forgotPasswordFormSchema(t),
    [t]
  )
  const loginForm = useForm<FormSchemaSignIn>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  })
  const ssoForm = useForm<ForgotPasswordFormSchemaProps>({
    resolver: zodResolver(ssoSchema),
    defaultValues: {
      email: "",
    },
  })
  const isLoginPending = loginForm.formState.isSubmitting
  const handleLoginSubmit = loginForm.handleSubmit(
    async (values) => {
      const { success, isConnectionError, isFirstLogin } =
        await login(values)
      if (success) {
        // Toast de sucesso removido - confirmação é ver o dashboard
        // Se não tiver permissão, o PermissionContext mostra o erro
      } else if (isFirstLogin) {
        toast({
          title: t("auth.firstLoginDetected"),
          variant: "default",
        })
      } else if (isConnectionError) {
        toast({
          title: t("auth.maintenanceError"),
          variant: "destructive",
        })
      } else {
        toast({
          title: t("auth.loginError"),
          variant: "destructive",
        })
      }
    }
  )
  const handleSSOSubmit = ssoForm.handleSubmit(
    async (values) => {
      const email = values.email.trim()
      await fetchSSOUrl(StringHelper.getEmailDomain(email))
    }
  )
  return (
    <div className="w-full max-w-[400px] space-y-8">
      {/* Logo e Título */}
      <div className="flex flex-col items-center space-y-6">
        <div className="relative flex h-16 w-16 items-center justify-center rounded-2xl p-3">
          <div
            className="absolute inset-0 rounded-2xl"
            style={{
              backgroundColor: "var(--whitelabel-primary)",
              opacity: 0.1,
            }}
          />
          <BrandLogo
            brand={brand}
            className="relative z-10 h-10 w-10"
            customLogo={configuration?.merchant?.logo}
            customAlt={configuration?.merchant?.fantasyName}
          />
        </div>
        <div className="space-y-2 text-center">
          <h1 className="page-title tracking-tight text-foreground">
            {isSSO
              ? t("auth.welcomeSSO")
              : t("auth.welcome")}
          </h1>
          <p className="text-sm text-muted-foreground">
            {isSSO
              ? t("auth.ssoDescription")
              : t("auth.loginDescription")}
          </p>
        </div>
      </div>
      {/* Formulário */}
      <div className="flex min-h-[380px] flex-col rounded-2xl border border-border/50 bg-card p-8 shadow-lg shadow-black/5">
        {isSSO ? (
          <div className="flex flex-1 flex-col justify-start">
            <div className="mb-6 flex items-center justify-center gap-3 rounded-xl bg-muted/50 p-4">
              <BuildingIcon className="h-5 w-5 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">
                Autenticação via provedor de identidade
              </span>
            </div>
            <Form {...ssoForm}>
              <form
                onSubmit={handleSSOSubmit}
                className="space-y-5"
              >
                <FormField
                  name="email"
                  control={ssoForm.control}
                  render={({ field }) => (
                    <FormItem className="space-y-2">
                      <FormLabel className="text-sm font-medium text-foreground">
                        {t("common.email")}
                      </FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          type="email"
                          placeholder={t(
                            "auth.emailCorporatePlaceholder"
                          )}
                          disabled={isSSOPending}
                          className="h-12 rounded-xl border-border/50 bg-background px-4 transition-all focus:border-primary focus:ring-2 focus:ring-primary/20"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <div className="flex flex-col gap-3 pt-2">
                  <Button
                    type="submit"
                    className="btn-whitelabel h-12 w-full rounded-xl text-base font-medium shadow-lg transition-all hover:shadow-xl"
                    disabled={isSSOPending}
                  >
                    {isSSOPending ? (
                      <Loader2 className="h-5 w-5 animate-spin" />
                    ) : (
                      <>
                        {t("auth.continue")}
                        <ArrowRightIcon className="ml-2 h-4 w-4" />
                      </>
                    )}
                  </Button>
                  <Button
                    variant="outline"
                    type="button"
                    className="h-12 w-full rounded-xl border-border/50 transition-all hover:bg-muted"
                    onClick={handleToggleToLogin}
                  >
                    <ArrowLeftIcon className="mr-2 h-4 w-4" />
                    {t("auth.backToLogin")}
                  </Button>
                </div>
              </form>
            </Form>
          </div>
        ) : (
          <div className="flex flex-1 flex-col">
            <Form {...loginForm}>
              <form
                onSubmit={handleLoginSubmit}
                className="space-y-5"
              >
                <FormField
                  name="email"
                  control={loginForm.control}
                  render={({ field }) => (
                    <FormItem className="space-y-2">
                      <FormLabel className="text-sm font-medium text-foreground">
                        {t("common.email")}
                      </FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          type="email"
                          placeholder={t(
                            "auth.emailPlaceholder"
                          )}
                          disabled={isLoginPending}
                          className="h-12 rounded-xl border-border/50 bg-background px-4 transition-all focus:border-primary focus:ring-2 focus:ring-primary/20"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  name="password"
                  control={loginForm.control}
                  render={({ field }) => (
                    <FormItem className="space-y-2">
                      <div className="flex items-center justify-between">
                        <FormLabel className="text-sm font-medium text-foreground">
                          {t("auth.passwordPlaceholder")}
                        </FormLabel>
                        <Link
                          to={
                            FRONTEND_ROUTES.FORGOT_PASSWORD
                              .href
                          }
                          className="text-xs font-medium hover:underline"
                          style={{
                            color:
                              "var(--whitelabel-primary)",
                          }}
                        >
                          {t("auth.forgotPassword")}
                        </Link>
                      </div>
                      <FormControl>
                        <PasswordInput
                          {...field}
                          placeholder="••••••••"
                          disabled={isLoginPending}
                          className="h-12 rounded-xl border-border/50 bg-background px-4 transition-all focus:border-primary focus:ring-2 focus:ring-primary/20"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <Button
                  type="submit"
                  className="btn-whitelabel h-12 w-full rounded-xl text-base font-medium shadow-lg transition-all hover:shadow-xl"
                  disabled={isLoginPending}
                >
                  {isLoginPending ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : (
                    <>
                      {t("auth.login")}
                      <ArrowRightIcon className="ml-2 h-4 w-4" />
                    </>
                  )}
                </Button>
              </form>
            </Form>
            {/* Divider */}
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-border/50" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-card px-3 text-muted-foreground">
                  ou continue com
                </span>
              </div>
            </div>
            {/* SSO Button */}
            <Button
              type="button"
              variant="outline"
              className="h-12 w-full rounded-xl border-border/50 bg-background text-foreground transition-all hover:bg-muted hover:shadow-md"
              onClick={handleToggleToSSO}
            >
              <BuildingIcon className="mr-2 h-5 w-5 text-muted-foreground" />
              <span className="font-medium">
                {t("auth.loginWithProvider")}
              </span>
            </Button>
          </div>
        )}
      </div>
      {/* Footer */}
      <p className="text-center text-xs text-muted-foreground">
        {isSSO ? (
          t("auth.ssoFooter")
        ) : (
          <>
            {t("auth.termsText")}{" "}
            <a
              href="https://pay2b.com.br/termos-condicoes"
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium hover:underline"
              style={{ color: "var(--whitelabel-primary)" }}
            >
              {t("auth.termsOfUse")}
            </a>{" "}
            {t("auth.and")}{" "}
            <a
              href="https://pay2b.com.br/politica-de-privacidade"
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium hover:underline"
              style={{ color: "var(--whitelabel-primary)" }}
            >
              {t("auth.privacyPolicy")}
            </a>
          </>
        )}
      </p>
    </div>
  )
}
export default LoginForm
